import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { ChevronRight, Calendar } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

export default function CierreMensualForm({ initialData, onSubmit }) {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const defaultMonth = lastMonth.toISOString().slice(0, 7);

  const [formData, setFormData] = useState({
    mes_cierre: initialData?.mes_cierre || defaultMonth,
    incluir_envio_gestoria: initialData?.incluir_envio_gestoria ?? true,
    observaciones: initialData?.observaciones || '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.mes_cierre) newErrors.mes_cierre = 'Requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSubmit(formData);
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Alert className="bg-rose-50 border-rose-200">
        <Calendar className="h-4 w-4 text-rose-600" />
        <AlertDescription className="text-rose-800">
          El cierre mensual prepara toda la documentación contable del mes para su revisión y envío a la gestoría.
          Incluye verificación de facturas emitidas, gastos registrados y conciliación bancaria.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div>
          <Label>Mes a Cerrar *</Label>
          <Input
            type="month"
            value={formData.mes_cierre}
            onChange={(e) => handleChange('mes_cierre', e.target.value)}
            className={errors.mes_cierre ? 'border-red-500' : ''}
            data-testid="input-mes-cierre"
          />
          {errors.mes_cierre && <p className="text-red-500 text-xs mt-1">{errors.mes_cierre}</p>}
          {formData.mes_cierre && (
            <p className="text-sm text-slate-600 mt-2">
              Cierre contable de: <strong>{formatMonth(formData.mes_cierre)}</strong>
            </p>
          )}
        </div>

        <div className="bg-slate-50 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-slate-900">Este cierre incluirá:</h4>
          <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
            <li>Verificación de todas las facturas emitidas del mes</li>
            <li>Revisión de todos los gastos registrados</li>
            <li>Conciliación bancaria del período</li>
            <li>Generación del pack contable (facturas + gastos + extractos)</li>
            <li>Informe de resumen mensual</li>
          </ul>
        </div>

        <div className="flex items-center space-x-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <Checkbox
            id="incluir_envio_gestoria"
            checked={formData.incluir_envio_gestoria}
            onCheckedChange={(checked) => handleChange('incluir_envio_gestoria', checked)}
          />
          <Label htmlFor="incluir_envio_gestoria" className="cursor-pointer text-amber-900">
            <span className="font-medium">Incluir envío automático a gestoría</span>
            <p className="text-sm text-amber-700 mt-1">
              El pack contable se enviará automáticamente a la gestoría configurada en tu cuenta
            </p>
          </Label>
        </div>

        <div>
          <Label>Observaciones</Label>
          <Textarea
            value={formData.observaciones}
            onChange={(e) => handleChange('observaciones', e.target.value)}
            placeholder="Información adicional relevante para el cierre (ej: ajustes pendientes, notas especiales)..."
            rows={3}
          />
        </div>
      </div>

      <Button type="submit" className="w-full" data-testid="btn-submit-form">
        Continuar <ChevronRight className="w-4 h-4 ml-2" />
      </Button>
    </form>
  );
}
